import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Users, ShieldCheck, Stethoscope, Flag, Gavel, Activity, UserPlus, CreditCard, Sparkles, TrendingUp, Percent } from "lucide-react";

const RISK_TINT = { 1: "bg-blue-500/15 text-blue-600", 2: "bg-amber-500/15 text-amber-600", 3: "bg-orange-500/15 text-orange-600", 4: "bg-destructive/15 text-destructive" };

export default function Admin() {
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [pending, setPending] = useState([]);
  const [logs, setLogs] = useState([]);
  const [flagged, setFlagged] = useState([]);
  const [appeals, setAppeals] = useState([]);
  const [reports, setReports] = useState([]);

  const loadAll = () => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/users").then(({ data }) => setUsers(data.users || [])).catch(() => {});
    api.get("/admin/professionals/pending").then(({ data }) => setPending(data.professionals || [])).catch(() => {});
    api.get("/admin/moderation").then(({ data }) => setLogs(data.logs || [])).catch(() => {});
    api.get("/admin/posts/flagged").then(({ data }) => setFlagged(data.posts || [])).catch(() => {});
    api.get("/admin/appeals").then(({ data }) => setAppeals(data.appeals || [])).catch(() => {});
    api.get("/admin/reports").then(({ data }) => setReports(data.reports || [])).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const decide = async (id, decision) => {
    try { await api.post(`/admin/professionals/${id}/decision`, { decision }); toast.success(`Professional ${decision}d`); loadAll(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const postAction = async (id, action) => { await api.post(`/admin/posts/${id}/action`, { action }); toast.success("Updated"); loadAll(); };
  const appealDecide = async (id, decision) => { await api.post(`/admin/appeals/${id}/decision`, { decision }); toast.success("Appeal resolved"); loadAll(); };
  const resolveReport = async (id, action) => { await api.post(`/admin/reports/${id}/resolve`, { action }); toast.success("Report resolved"); loadAll(); };

  const cards = [
    { icon: Users, label: "Users", value: stats.users },
    { icon: Stethoscope, label: "Verified pros", value: stats.professionals },
    { icon: ShieldCheck, label: "Pending pros", value: stats.pending_professionals },
    { icon: Flag, label: "Flagged posts", value: stats.flagged_posts },
    { icon: Activity, label: "Moderation events", value: stats.moderation_events },
    { icon: Gavel, label: "Open appeals", value: stats.open_appeals },
  ];

  const currencySymbol = { eur: "€", usd: "$", gbp: "£" }[stats.currency] || "€";
  const fmtMoney = (n) => `${currencySymbol}${(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const growthCards = [
    { icon: UserPlus, label: "New today", value: stats.new_today, tint: "text-emerald-600" },
    { icon: TrendingUp, label: "New this week", value: stats.new_this_week, tint: "text-emerald-600" },
    { icon: TrendingUp, label: "New this month", value: stats.new_this_month, tint: "text-emerald-600" },
  ];
  const revenueCards = [
    { icon: CreditCard, label: "Paid subscribers", value: stats.paid_subscribers, tint: "text-primary" },
    { icon: Sparkles, label: "On trial", value: stats.trialing_users, tint: "text-amber-600" },
    { icon: Users, label: "Free / expired", value: stats.free_users, tint: "text-muted-foreground" },
    { icon: TrendingUp, label: "Est. MRR", value: fmtMoney(stats.mrr), tint: "text-emerald-600", isMoney: true },
    { icon: Percent, label: "Trial → paid", value: `${stats.conversion_rate ?? 0}%`, tint: "text-primary", isMoney: true },
  ];

  const chartData = (stats.signups_daily || []).map((d) => ({
    ...d,
    label: new Date(d.date).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
  }));

  return (
    <div>
      <h1 className="font-serif text-3xl font-semibold sm:text-4xl">Admin dashboard</h1>
      <p className="mt-2 text-muted-foreground">Safety, verification and community oversight.</p>

      <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        {cards.map((c, i) => (
          <motion.div key={c.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.04 }}
                      className="ambient-shadow rounded-2xl border border-border/60 bg-card p-4">
            <c.icon className="h-4 w-4 text-primary" strokeWidth={1.5} />
            <div className="mt-2 font-serif text-2xl">{c.value ?? 0}</div>
            <div className="text-xs text-muted-foreground">{c.label}</div>
          </motion.div>
        ))}
      </div>

      {/* Growth & revenue */}
      <div className="mt-8 grid gap-6 lg:grid-cols-3">
        <div className="ambient-shadow rounded-2xl border border-border/60 bg-card p-5 lg:col-span-2" data-testid="signup-growth-chart">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-serif text-lg font-semibold">New sign-ups</h2>
              <p className="text-xs text-muted-foreground">Registrations over the last 30 days</p>
            </div>
            <Badge variant="outline" className="rounded-full text-xs">+{stats.new_this_month ?? 0} this month</Badge>
          </div>
          <div className="mt-4 h-56 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="signupFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} interval={4} tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} width={28} />
                <Tooltip
                  contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", fontSize: 12 }}
                  labelStyle={{ color: "hsl(var(--foreground))" }}
                  formatter={(v) => [`${v} sign-up${v === 1 ? "" : "s"}`, ""]}
                />
                <Area type="monotone" dataKey="count" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#signupFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 flex flex-wrap gap-4">
            {growthCards.map((c) => (
              <div key={c.label} className="flex items-center gap-2">
                <c.icon className={`h-4 w-4 ${c.tint}`} strokeWidth={1.5} />
                <span className="font-serif text-lg">{c.value ?? 0}</span>
                <span className="text-xs text-muted-foreground">{c.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 content-start" data-testid="revenue-cards">
          {revenueCards.map((c, i) => (
            <motion.div key={c.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.04 }}
                        className={`ambient-shadow rounded-2xl border border-border/60 bg-card p-4 ${i === revenueCards.length - 1 && revenueCards.length % 2 ? "col-span-2" : ""}`}>
              <c.icon className={`h-4 w-4 ${c.tint}`} strokeWidth={1.5} />
              <div className={`mt-2 font-serif ${c.isMoney ? "text-xl" : "text-2xl"}`}>{c.value ?? 0}</div>
              <div className="text-xs text-muted-foreground">{c.label}</div>
            </motion.div>
          ))}
        </div>
      </div>

      <Tabs defaultValue="verification" className="mt-8">
        <TabsList className="flex-wrap rounded-full bg-secondary p-1">
          <TabsTrigger value="verification" className="rounded-full" data-testid="tab-verification">Verification</TabsTrigger>
          <TabsTrigger value="moderation" className="rounded-full" data-testid="tab-moderation">Moderation</TabsTrigger>
          <TabsTrigger value="flagged" className="rounded-full" data-testid="tab-flagged">Flagged posts</TabsTrigger>
          <TabsTrigger value="reports" className="rounded-full" data-testid="tab-reports">Reports{reports.filter((r) => r.status === "open").length ? ` (${reports.filter((r) => r.status === "open").length})` : ""}</TabsTrigger>
          <TabsTrigger value="appeals" className="rounded-full" data-testid="tab-appeals">Appeals</TabsTrigger>
          <TabsTrigger value="users" className="rounded-full" data-testid="tab-users">Users</TabsTrigger>
        </TabsList>

        <TabsContent value="verification" className="mt-6 space-y-3">
          {pending.length === 0 && <p className="text-sm text-muted-foreground">No pending applications.</p>}
          {pending.map((p) => (
            <div key={p.pro_id} className="ambient-shadow rounded-2xl border border-border/60 bg-card p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-medium">{p.name} · <span className="text-muted-foreground">{p.profession}</span></p>
                  <p className="text-sm text-muted-foreground">License: {p.license_number || "—"} · {p.registration || "—"} · {p.city}, {p.country}</p>
                  <p className="mt-1 text-sm">{p.bio}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="rounded-full" onClick={() => decide(p.pro_id, "approve")} data-testid={`approve-${p.pro_id}`}>Approve</Button>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => decide(p.pro_id, "request_info")} data-testid={`info-${p.pro_id}`}>Request info</Button>
                  <Button size="sm" variant="outline" className="rounded-full text-destructive" onClick={() => decide(p.pro_id, "reject")} data-testid={`reject-${p.pro_id}`}>Reject</Button>
                </div>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="moderation" className="mt-6 space-y-2">
          {logs.length === 0 && <p className="text-sm text-muted-foreground">No moderation events yet.</p>}
          {logs.map((l) => (
            <div key={l.log_id} className="flex items-start justify-between gap-3 rounded-2xl border border-border/60 bg-card p-4">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Badge className={`rounded-full ${RISK_TINT[l.risk_level] || "bg-secondary"}`}>Risk {l.risk_level}</Badge>
                  <span className="text-xs text-muted-foreground">{l.category} · {l.action}</span>
                </div>
                <p className="mt-1 line-clamp-2 text-sm">{l.content}</p>
                {l.reason && <p className="text-xs text-muted-foreground">{l.reason}</p>}
              </div>
              <span className="text-xs text-muted-foreground">{new Date(l.created_at).toLocaleDateString()}</span>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="flagged" className="mt-6 space-y-2">
          {flagged.length === 0 && <p className="text-sm text-muted-foreground">No flagged posts.</p>}
          {flagged.map((p) => (
            <div key={p.post_id} className="rounded-2xl border border-border/60 bg-card p-4">
              <div className="flex items-center gap-2">
                <Badge className={`rounded-full ${RISK_TINT[p.risk_level] || "bg-secondary"}`}>Risk {p.risk_level}</Badge>
                <span className="text-xs text-muted-foreground">{p.status}</span>
              </div>
              <p className="mt-2 text-sm">{p.content}</p>
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => postAction(p.post_id, "publish")} data-testid={`publish-${p.post_id}`}>Publish</Button>
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => postAction(p.post_id, "hide")} data-testid={`hide-${p.post_id}`}>Hide</Button>
                <Button size="sm" variant="outline" className="rounded-full text-destructive" onClick={() => postAction(p.post_id, "remove")} data-testid={`remove-${p.post_id}`}>Remove</Button>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="reports" className="mt-6 space-y-2">
          {reports.length === 0 && <p className="text-sm text-muted-foreground">No reports.</p>}
          {reports.map((r) => (
            <div key={r.report_id} className="rounded-2xl border border-border/60 bg-card p-4" data-testid={`report-${r.report_id}`}>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="rounded-full capitalize">{r.target_type}</Badge>
                <Badge className="rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-400 capitalize">{r.reason}</Badge>
                <span className="text-xs text-muted-foreground">{r.status}{r.created_at ? ` · ${new Date(r.created_at).toLocaleDateString()}` : ""}</span>
              </div>
              <p className="mt-2 text-sm">{r.content || "—"}</p>
              <p className="mt-1 text-xs text-muted-foreground">by {r.author_name || "Member"}{r.group ? ` · ${r.group}` : ""}</p>
              {r.status === "open" && (
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => resolveReport(r.report_id, "dismiss")} data-testid={`report-dismiss-${r.report_id}`}>Dismiss</Button>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => resolveReport(r.report_id, "hide_post")} data-testid={`report-hide-${r.report_id}`}>Hide post</Button>
                  <Button size="sm" variant="outline" className="rounded-full text-destructive" onClick={() => resolveReport(r.report_id, "remove_post")} data-testid={`report-remove-${r.report_id}`}>Remove post</Button>
                </div>
              )}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="appeals" className="mt-6 space-y-2">
          {appeals.length === 0 && <p className="text-sm text-muted-foreground">No appeals.</p>}
          {appeals.map((a) => (
            <div key={a.appeal_id} className="rounded-2xl border border-border/60 bg-card p-4">
              <p className="text-sm">{a.reason}</p>
              <div className="mt-2 flex items-center gap-2">
                <Badge variant="outline" className="rounded-full">{a.status}</Badge>
                {a.status === "pending" && (
                  <>
                    <Button size="sm" className="rounded-full" onClick={() => appealDecide(a.appeal_id, "granted")} data-testid={`grant-${a.appeal_id}`}>Grant</Button>
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => appealDecide(a.appeal_id, "denied")} data-testid={`deny-${a.appeal_id}`}>Deny</Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="users" className="mt-6">
          <div className="overflow-hidden rounded-2xl border border-border/60">
            {users.map((u) => (
              <div key={u.user_id} className="flex items-center justify-between border-b border-border/40 bg-card p-4 last:border-0">
                <div><p className="text-sm font-medium">{u.name}</p><p className="text-xs text-muted-foreground">{u.email}</p></div>
                <Badge variant="outline" className="rounded-full capitalize">{u.role}</Badge>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
